import { Checkbox, DefaultButton, MessageBar, MessageBarType, PrimaryButton, Spinner, Stack, Text, TextField } from "@fluentui/react"
import { useEffect, useState } from "react"
import { medicalRequest, QuestionnaireAccountHandoff, QuestionnaireInvitation } from "../services/medicalQuestionnaireService"

export function MedicalQuestionnaireStart() {
	const [token] = useState(() => {
		return new URLSearchParams(window.location.hash.slice(1)).get("invite") || ""
	})
	const [invitation, setInvitation] = useState<QuestionnaireInvitation>()
	const [busy, setBusy] = useState(false)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string>()
	const [code, setCode] = useState("")
	const [requested, setRequested] = useState(false)
	const [retryAt, setRetryAt] = useState(0)
	const [clock, setClock] = useState(Date.now())
	const [proof, setProof] = useState("")
	const [accepted, setAccepted] = useState(false)
	const [handoff, setHandoff] = useState<QuestionnaireAccountHandoff>()

	useEffect(() => {
		const controller = new AbortController()
		window.history.replaceState(null, "", window.location.pathname)
		async function load() {
			try {
				if (!token) throw new Error("Open the personal link in your invitation email to begin. If you refreshed this page, reopen that email link.")
				const result = await medicalRequest<QuestionnaireInvitation>("invitation", { token }, false, controller.signal)
				if (!controller.signal.aborted) setInvitation(result)
			} catch (failure) {
				if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : "This invitation could not be opened.")
			} finally {
				if (!controller.signal.aborted) setLoading(false)
			}
		}
		void load()
		return () => controller.abort()
	}, [token])

	useEffect(() => {
		if (retryAt <= Date.now()) return
		const timer = window.setInterval(() => setClock(Date.now()), 1000)
		return () => window.clearInterval(timer)
	}, [retryAt])

	async function requestCode() {
		setBusy(true)
		setError(undefined)
		try {
			const result = await medicalRequest<{ verified: boolean }>("otp/request", { token }, false)
			setInvitation(current => ({ ...current, verified: result.verified }))
			setRequested(true)
			setCode("")
			setRetryAt(Date.now() + 60000)
			setClock(Date.now())
		} catch (failure) {
			setError(failure instanceof Error ? failure.message : "The code could not be sent.")
		} finally {
			setBusy(false)
		}
	}
	async function verify() {
		setBusy(true)
		setError(undefined)
		try {
			const result = await medicalRequest<{ proof: string }>("otp/verify", { token, code }, false)
			setProof(result.proof || "")
			setInvitation(current => ({ ...current, verified: true }))
			setCode("")
		} catch (failure) {
			setError(failure instanceof Error ? failure.message : "The code could not be verified.")
		} finally {
			setBusy(false)
		}
	}
	async function continueToAccount() {
		setBusy(true)
		setError(undefined)
		try {
			setHandoff(await medicalRequest("onboarding", { token, proof, accepted }, false))
		} catch (failure) {
			setError(failure instanceof Error ? failure.message : "Account setup could not be started.")
		} finally {
			setBusy(false)
		}
	}
	const retrySeconds = Math.max(0, Math.ceil((retryAt - clock) / 1000))
	return (
		<Stack
			tokens={{ childrenGap: 20 }}
			styles={{ root: { maxWidth: 560, width: "100%", margin: "32px auto", padding: 24, border: "1px solid #d2dce5", borderRadius: 12, boxSizing: "border-box" } }}>
			<Text as="h1" variant="xxLarge">
				Welcome to ACMG onboarding
			</Text>
			{!handoff && <Text>Your personal invitation helps protect your household's information. First, verify access to the email address on your employee record.</Text>}
			{loading && <Spinner label="Checking your invitation..." />}
			{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
			{proof ? (
				<>
					<MessageBar messageBarType={MessageBarType.success} isMultiline>
						{handoff
							? "Your email has been verified and your sample privacy acknowledgment has been saved."
							: "Your email has been verified. Accept the sample privacy statement to continue. We will check whether your linked portal account needs registration or sign-in."}
					</MessageBar>
					{!handoff && (
						<Stack tokens={{ childrenGap: 12 }}>
							<Text as="h2" variant="large">
								Privacy and consent preview
							</Text>
							<MessageBar messageBarType={MessageBarType.warning} isMultiline>
								DRAFT PLACEHOLDER ONLY. This Lorem ipsum text is not a privacy notice, consent, or medical authorization. Use synthetic test data only, never real enrollment or medical
								information.
							</MessageBar>
							<Text>
								Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer posuere erat a ante. Curabitur blandit tempus porttitor. This sample copy is for interface review only
								and does not describe how information is used or protected.
							</Text>
							<Checkbox
								label="For this synthetic-data sandbox test, I acknowledge the sample privacy text. This is not approved legal consent."
								checked={accepted}
								disabled={busy}
								onChange={(_, value) => setAccepted(!!value)}
							/>
							<PrimaryButton text="Continue to portal account setup" disabled={busy || !accepted} onClick={continueToAccount} />
						</Stack>
					)}
					{handoff && (
						<Stack tokens={{ childrenGap: 12 }}>
							<Text as="h2" variant="large">
								{handoff.registered ? "You already have a portal login" : "Create your portal login"}
							</Text>
							<Text>{handoff.message}</Text>
							<Text>
								{handoff.registered
									? "Use your existing portal login. If you forgot your password, use the password reset option on the sign-in page."
									: "Your portal invitation is ready. Use it to complete registration for your linked account; you do not need to request another invitation."}
							</Text>
							<Text>After registration or sign-in, you will return directly to your household questionnaire. Medical answers remain unavailable until you are signed in.</Text>
							{handoff.registered === true ? (
								<PrimaryButton text="Sign in to your existing account" href={handoff.signInUrl} />
							) : (
								<PrimaryButton text="Register and continue to questionnaire" href={handoff.registrationUrl} />
							)}
						</Stack>
					)}
				</>
			) : (
				invitation && (
					<>
						{invitation.verified && <MessageBar>Request a fresh verification code to continue to account setup. A previous verification alone does not grant access.</MessageBar>}
						<Text>
							A verification code will be sent to <strong>{invitation.emailHint}</strong>. Codes expire after 10 minutes; only the newest code works.
						</Text>
						<DefaultButton
							text={retrySeconds ? `Request another code in ${retrySeconds}s` : requested ? "Send a new code" : "Email me a verification code"}
							disabled={busy || retrySeconds > 0}
							onClick={requestCode}
						/>
						{requested && (
							<form
								onSubmit={event => {
									event.preventDefault()
									void verify()
								}}>
								<Stack tokens={{ childrenGap: 12 }}>
									<TextField
										label="Six-digit verification code"
										required
										autoComplete="one-time-code"
										inputMode="numeric"
										maxLength={6}
										value={code}
										onChange={(_, value) => setCode((value || "").replace(/\D/g, ""))}
										disabled={busy}
									/>
									<PrimaryButton type="submit" text={busy ? "Verifying..." : "Verify email"} disabled={busy || code.length !== 6} />
								</Stack>
							</form>
						)}
						<Text variant="small">Do not forward your link or verification code. Contact ACMG if this invitation was not intended for you.</Text>
					</>
				)
			)}
		</Stack>
	)
}
